// src/lib/nav.ts
//
// The navigation IS the permission registry. Every item names the page key it needs;
// the sidebar filters itself to what the signed-in user can reach, which is why one
// application serves the CEO, a technician, a resident and a vendor without any
// role-specific routing.
//
// Page keys here must match src/config/permissionRegistry.ts on the backend.

export interface NavItem {
  label: string;
  path: string;
  page: string;          // permission page key
  icon: string;          // lucide icon name
  /** Only show when the user's data scope is this (e.g. a resident's "My home"). */
  onlyScope?: 'OWN';
  /** Hide when the user's data scope is this (list pages are noise for a single-record scope). */
  hideForScope?: 'OWN';
}

export interface NavGroup {
  group: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    group: 'Overview',
    items: [
      { label: 'Dashboard', path: '/app', page: 'dashboard', icon: 'LayoutDashboard' },
      { label: 'My home', path: '/app/my-home', page: 'residents', icon: 'Home', onlyScope: 'OWN' },
    ],
  },
  {
    group: 'Portfolio',
    items: [
      { label: 'Properties', path: '/app/properties', page: 'properties', icon: 'Building2' },
      { label: 'Units', path: '/app/units', page: 'units', icon: 'DoorOpen' },
      { label: 'Listings', path: '/app/listings', page: 'listings', icon: 'Megaphone' },
    ],
  },
  {
    group: 'Leasing',
    items: [
      { label: 'Leads', path: '/app/leads', page: 'leads', icon: 'UserPlus' },
      { label: 'Viewings', path: '/app/viewings', page: 'viewings', icon: 'CalendarClock' },
      { label: 'Applications', path: '/app/applications', page: 'applications', icon: 'FileText' },
      { label: 'Tenancies', path: '/app/tenancies', page: 'tenancies', icon: 'FileSignature' },
      { label: 'Deposits', path: '/app/deposits', page: 'deposits', icon: 'ShieldCheck' },
    ],
  },
  {
    group: 'Residents',
    items: [
      { label: 'Residents', path: '/app/residents', page: 'residents', icon: 'Users', hideForScope: 'OWN' },
    ],
  },
  {
    group: 'Maintenance',
    items: [
      { label: 'Work orders', path: '/app/work-orders', page: 'workOrders', icon: 'Wrench' },
      { label: 'Assets', path: '/app/assets', page: 'assets', icon: 'Package' },
      { label: 'Compliance', path: '/app/compliance', page: 'compliance', icon: 'BadgeCheck' },
      { label: 'Vendors', path: '/app/vendors', page: 'vendors', icon: 'HardHat' },
      { label: 'Inspections', path: '/app/inspections', page: 'inspections', icon: 'ClipboardCheck' },
    ],
  },
  {
    group: 'Finance',
    items: [
      { label: 'Invoices', path: '/app/invoices', page: 'invoices', icon: 'ReceiptText' },
      { label: 'Credit & debit notes', path: '/app/notes', page: 'creditDebitNotes', icon: 'FileMinus2' },
      { label: 'Bills & expenses', path: '/app/bills', page: 'bills', icon: 'FileInput' },
      { label: 'Payments', path: '/app/payments', page: 'payments', icon: 'Banknote' },
      { label: 'Recurring', path: '/app/recurring', page: 'recurringInvoices', icon: 'RefreshCw' },
      { label: 'Banking', path: '/app/banking', page: 'banking', icon: 'Landmark' },
      { label: 'Chart of accounts', path: '/app/accounts', page: 'chartOfAccounts', icon: 'ListTree' },
      { label: 'Journals', path: '/app/journals', page: 'journals', icon: 'BookOpen' },
      { label: 'Fixed assets', path: '/app/fixed-assets', page: 'fixedAssets', icon: 'Boxes' },
      { label: 'Budgets', path: '/app/budgets', page: 'budgets', icon: 'Target' },
      { label: 'Periods', path: '/app/periods', page: 'periods', icon: 'CalendarRange' },
      { label: 'VAT returns', path: '/app/vat', page: 'taxReports', icon: 'Percent' },
      { label: 'CIS', path: '/app/cis', page: 'cis', icon: 'HardHat' },
      { label: 'Statements', path: '/app/statements', page: 'glReports', icon: 'FileBarChart' },
      { label: 'Finance settings', path: '/app/finance-settings', page: 'financeSettings', icon: 'SlidersHorizontal' },
    ],
  },
  {
    group: 'Reports',
    items: [{ label: 'Reports', path: '/app/reports', page: 'reports', icon: 'ChartBar' }],
  },
  {
    group: 'Settings',
    items: [
      { label: 'Users', path: '/app/users', page: 'users', icon: 'UserCog' },
      { label: 'Roles & permissions', path: '/app/roles', page: 'roles', icon: 'KeyRound' },
      { label: 'Companies', path: '/app/entities', page: 'entities', icon: 'Landmark' },
      { label: 'Audit log', path: '/app/audit', page: 'auditLog', icon: 'ScrollText' },
    ],
  },
];
